import { ChevronDown, Filter, RotateCcw } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { transportApi } from '../transport/api'
import { buildAreaCascadeLevels } from '../transport/areaHierarchy'
import {
  areaScopeLabel,
  areaTypeLabel,
  contentFor,
  localizedName,
} from '../transport/content'
import { TransportNetworkMap } from '../transport/components/TransportNetworkMap'
import { TransportSEO } from '../transport/components/TransportSEO'
import type {
  AreaSummary,
  Locale,
  NetworkSummary,
  TransportMapLine,
} from '../transport/types'

function lineCountLabel(count: number, locale: Locale) {
  return locale === 'ar'
    ? `${count} خط ظاهر على الخريطة`
    : `${count} lines shown on map`
}

function networkLabel(network: NetworkSummary, locale: Locale) {
  const name = localizedName(network, locale) || network.code
  const governorate = localizedName(network.primaryArea, locale) || network.primaryArea.code
  return `${name} — ${governorate}`
}

export function MapPage({ locale }: { locale: Locale }) {
  const content = contentFor(locale)

  const [areas, setAreas] = useState<AreaSummary[]>([])
  const [areasLoading, setAreasLoading] = useState(true)
  const [areasFailed, setAreasFailed] = useState(false)
  const [areaPath, setAreaPath] = useState<string[]>([])

  const [networks, setNetworks] = useState<NetworkSummary[]>([])
  const [networkId, setNetworkId] = useState('')
  const [networksLoading, setNetworksLoading] = useState(true)
  const [networksFailed, setNetworksFailed] = useState(false)

  const [lines, setLines] = useState<TransportMapLine[]>([])
  const [mapLoading, setMapLoading] = useState(true)
  const [mapFailed, setMapFailed] = useState(false)
  const [filtersOpen, setFiltersOpen] = useState(false)

  useEffect(() => {
    let current = true
    setAreasLoading(true)
    setAreasFailed(false)

    void transportApi.areaOptions()
      .then((items) => {
        if (!current) return
        setAreas(items)

        const rootCountries = items.filter(
          (area) => area.parentId === null && area.type === 'Country',
        )
        if (rootCountries.length === 1) setAreaPath([rootCountries[0].id])
      })
      .catch(() => {
        if (!current) return
        setAreas([])
        setAreaPath([])
        setAreasFailed(true)
      })
      .finally(() => {
        if (current) setAreasLoading(false)
      })

    return () => { current = false }
  }, [])

  const areaLevels = useMemo(
    () => buildAreaCascadeLevels(areas, areaPath),
    [areaPath, areas],
  )

  const selectedAreaId = areaPath.at(-1) ?? ''
  const defaultCountryId = useMemo(
    () => areas.find((area) => area.parentId === null && area.type === 'Country')?.id ?? '',
    [areas],
  )
  const hiddenRootLevels = defaultCountryId ? 1 : 0
  const visibleAreaLevels = useMemo(
    () => areaLevels.slice(hiddenRootLevels),
    [areaLevels, hiddenRootLevels],
  )

  useEffect(() => {
    if (areasLoading) return undefined

    let current = true
    setNetworkId('')
    setNetworks([])
    setNetworksLoading(true)
    setNetworksFailed(false)

    void transportApi.networkOptions(selectedAreaId || undefined)
      .then((items) => {
        if (current) setNetworks(items)
      })
      .catch(() => {
        if (!current) return
        setNetworks([])
        setNetworksFailed(true)
      })
      .finally(() => {
        if (current) setNetworksLoading(false)
      })

    return () => { current = false }
  }, [areasLoading, selectedAreaId])

  useEffect(() => {
    if (areasLoading) return undefined

    let current = true
    setMapLoading(true)
    setMapFailed(false)

    void transportApi.map({
      administrativeAreaId: selectedAreaId || undefined,
      transportNetworkId: networkId || undefined,
    })
      .then((items) => {
        if (current) setLines(items)
      })
      .catch(() => {
        if (!current) return
        setLines([])
        setMapFailed(true)
      })
      .finally(() => {
        if (current) setMapLoading(false)
      })

    return () => { current = false }
  }, [areasLoading, networkId, selectedAreaId])

  const reset = () => {
    setAreaPath(defaultCountryId ? [defaultCountryId] : [])
    setNetworkId('')
  }

  const networkPlaceholder = networksLoading
    ? content.labels.loading
    : networksFailed
      ? content.labels.networksUnavailable
      : networks.length
        ? content.labels.allNetworks
        : content.labels.noNetworks

  return (
    <>
      <TransportSEO locale={locale} />
      <section className="map-app" aria-busy={mapLoading}>
        <h1 className="sr-only">
          {locale === 'ar' ? 'خريطة خطوط باصات سوريا' : 'Syria bus lines map'}
        </h1>

        <div className="map-app__filters">
          <div className="map-app__filters-title map-app__filters-title--desktop">
            <Filter aria-hidden="true" />
          </div>

          <button
            type="button"
            className="map-app__filters-toggle"
            aria-expanded={filtersOpen}
            aria-controls="map-filters-body"
            aria-label={filtersOpen
              ? content.labels.hideFilters
              : content.labels.showFilters}
            onClick={() => setFiltersOpen((open) => !open)}
          >
            <span className="map-app__filters-toggle-label">
              <Filter aria-hidden="true" />
            </span>
            <ChevronDown
              className={filtersOpen ? 'is-open' : ''}
              aria-hidden="true"
            />
          </button>

          <div
            id="map-filters-body"
            className={`map-app__filters-body ${filtersOpen ? 'is-open' : ''}`}
          >
            <div className="map-app__area-cascade">
              {areasLoading && (
                <label>
                  <span>{areaTypeLabel('Governorate', locale)}</span>
                  <select disabled value="">
                    <option value="">{content.labels.loading}</option>
                  </select>
                </label>
              )}

              {!areasLoading && areasFailed && (
                <label>
                  <span>{areaTypeLabel('Governorate', locale)}</span>
                  <select disabled value="">
                    <option value="">{content.labels.areasUnavailable}</option>
                  </select>
                </label>
              )}

              {!areasLoading && !areasFailed && visibleAreaLevels.map((level, visibleIndex) => {
                const pathIndex = visibleIndex + hiddenRootLevels
                const typeLabel = level.options[0]
                  ? areaTypeLabel(level.options[0].type, locale)
                  : content.labels.area

                return (
                  <label key={`${pathIndex}-${level.parent?.id ?? 'root'}`}>
                    <span>{typeLabel}</span>
                    <select
                      value={level.value}
                      onChange={(event) => {
                        const next = areaPath.slice(0, pathIndex)
                        if (event.target.value) next.push(event.target.value)
                        setAreaPath(next)
                      }}
                    >
                      <option value="">
                        {level.parent?.type === 'Country'
                          ? content.labels.allGovernorates
                          : areaScopeLabel(level.parent, locale)}
                      </option>
                      {level.options.map((area) => (
                        <option key={area.id} value={area.id}>
                          {localizedName(area, locale) || area.code}
                        </option>
                      ))}
                    </select>
                  </label>
                )
              })}
            </div>

            <label className="map-app__network-filter">
              <span>{content.labels.network}</span>
              <select
                value={networkId}
                disabled={networksLoading || networksFailed || networks.length === 0}
                onChange={(event) => setNetworkId(event.target.value)}
              >
                <option value="">{networkPlaceholder}</option>
                {networks.map((network) => (
                  <option key={network.id} value={network.id}>
                    {networkLabel(network, locale)}
                  </option>
                ))}
              </select>
            </label>

            <button
              type="button"
              className="map-app__reset"
              disabled={areaPath.length <= (defaultCountryId ? 1 : 0) && !networkId}
              onClick={reset}
            >
              <RotateCcw aria-hidden="true" />
              {content.labels.clearFilters}
            </button>

            <div className="map-app__count" aria-live="polite">
              {mapLoading
                ? content.labels.updatingMap
                : mapFailed
                  ? content.labels.loadError
                  : lineCountLabel(lines.length, locale)}
            </div>
          </div>
        </div>

        <div className="map-app__canvas">
          {mapFailed
            ? <div className="map-app__fallback">{content.labels.loadError}</div>
            : <TransportNetworkMap locale={locale} lines={lines} />}

          {mapLoading && !mapFailed && (
            <div className="map-app__loading-overlay" role="status">
              <span className="map-app__loading-spinner" aria-hidden="true" />
              <span>{content.labels.updatingMap}</span>
            </div>
          )}
        </div>
      </section>
    </>
  )
}
